import { useLayoutEffect, useMemo, type ReactNode } from 'react'
import { detectBrand } from './detectBrand'
import { BrandContext } from './BrandContext'
import type { BrandColors } from './types'

/** Converts a `BrandColors` object into indented CSS custom-property declarations. */
const colorsToCssVars = (colors: BrandColors): string =>
  [
    `--text: ${colors.text}`,
    `--text-h: ${colors.textH}`,
    `--bg: ${colors.bg}`,
    `--border: ${colors.border}`,
    `--code-bg: ${colors.codeBg}`,
    `--accent: ${colors.accent}`,
    `--accent-bg: ${colors.accentBg}`,
    `--accent-border: ${colors.accentBorder}`,
    `--social-bg: ${colors.socialBg}`,
    `--shadow: ${colors.shadow}`,
  ]
    .map((v) => `  ${v};`)
    .join('\n')

/** Builds the full brand `<style>` block (light + dark vars). */
const buildBrandStyleSheet = (brand: ReturnType<typeof detectBrand>): string => {
  const fontVars = [
    `  --sans: ${brand.fonts.sans};`,
    `  --heading: ${brand.fonts.heading};`,
    `  --mono: ${brand.fonts.mono};`,
  ].join('\n')

  return (
    `:root {\n${colorsToCssVars(brand.colors.light)}\n${fontVars}\n}\n\n` +
    `@media (prefers-color-scheme: dark) {\n  :root {\n${colorsToCssVars(brand.colors.dark)}\n  }\n}`
  )
}

/** Upserts a `<style>` element with the given `id` and `css` content. */
const injectStyleTag = (id: string, css: string): void => {
  let el = document.getElementById(id) as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = css
}

/** Injects a Google Fonts `<link>` element if one is not already present. */
const injectFontLink = (id: string, href: string): void => {
  if (!href || document.getElementById(id)) return
  const preconnect = document.createElement('link')
  preconnect.rel = 'preconnect'
  preconnect.href = 'https://fonts.googleapis.com'
  document.head.appendChild(preconnect)

  const el = document.createElement('link')
  el.id = id
  el.rel = 'stylesheet'
  el.href = href
  document.head.appendChild(el)
}

/** Removes the font `<link>` element with the given `id`. */
const removeFontLink = (id: string): void => {
  document.getElementById(id)?.remove()
}

/** MIME types for supported favicon file extensions. */
const FAVICON_MIME_TYPES: Record<string, string> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  ico: 'image/x-icon',
}

/** Upserts a `<link rel="icon">` element pointing at the given favicon URL. */
const setFavicon = (href: string): void => {
  let el = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
  if (!el) {
    el = document.createElement('link')
    el.rel = 'icon'
    document.head.appendChild(el)
  }
  const extension = href.split('.').pop() ?? ''
  el.type = FAVICON_MIME_TYPES[extension] ?? ''
  el.href = href
}

/**
 * Detects the active brand from `window.location.hostname`, injects brand
 * CSS variables and Google Fonts into `<head>`, and makes the brand config
 * available via `BrandContext`.
 */
export function BrandProvider({ children }: { children: ReactNode }) {
  const brand = useMemo(() => detectBrand(), [])

  useLayoutEffect(() => {
    injectStyleTag('brand-tokens', buildBrandStyleSheet(brand))
    document.documentElement.setAttribute('data-brand', brand.name.toLowerCase())
    document.title = brand.title
    setFavicon(brand.faviconUrl)
    injectFontLink('brand-font', brand.googleFontsUrl)

    return () => {
      removeFontLink('brand-font')
    }
  }, [brand])

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
}
